import React from 'react';
import Footer from '@/components/Footer';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-stone-50 flex flex-col font-sans">
      {/* Header */}
      <header className="bg-stone-50/90 backdrop-blur-sm border-b border-stone-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 sm:gap-2.5 hover:opacity-75 transition-opacity duration-150 flex-shrink-0">
              <img 
                src="/icon-192x192.png" 
                alt="PGastos" 
                className="h-7 sm:h-8 w-auto"
              />
              <span className="text-base sm:text-lg font-bold text-stone-900 tracking-tight">PGastos</span>
            </Link>
            <Link to="/">
              <Button variant="outline" size="sm" className="border-stone-300 hover:bg-stone-100 text-stone-700 text-xs sm:text-sm active:scale-95 transition-all duration-150 ease-spring cursor-pointer">
                <ArrowLeft className="w-4 h-4 mr-1.5" />
                Volver
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 w-full">
        <Card className="bg-white rounded-3xl border border-stone-200 shadow-sm p-7 sm:p-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-black text-stone-900 tracking-tight">Política de Privacidad</h1>
              <p className="text-xs text-stone-400 font-medium">Última actualización: Septiembre 2026</p>
            </div>
          </div>

          <div className="space-y-8 text-stone-600 text-sm sm:text-base leading-relaxed font-medium mt-8 divide-y divide-stone-100">
            <section className="pt-6 first:pt-0">
              <h2 className="text-lg font-bold text-stone-900 mb-3">1. Introducción</h2>
              <p>
                En PGastos ("nosotros", "nuestro" o "la aplicación"), operado por VJRDEV Company, 
                nos comprometemos a proteger tu privacidad. Esta Política de Privacidad explica cómo 
                recopilamos, usamos, divulgamos y protegemos tu información cuando utilizas nuestra 
                herramienta de gestión financiera personal.
              </p>
            </section>

            <section className="pt-6">
              <h2 className="text-lg font-bold text-stone-900 mb-3">2. Información que Recopilamos</h2>
              <h3 className="font-bold text-stone-800 mb-2">2.1 Información que Proporcionas</h3>
              <ul className="list-disc list-inside space-y-1.5 ml-2 text-stone-600 mb-4">
                <li>Nombre de usuario y correo electrónico</li>
                <li>Contraseña (encriptada con bcrypt)</li>
                <li>Datos de transacciones registradas (ingresos, gastos, cuentas)</li>
                <li>Categorías personalizadas y objetivos de ahorro</li>
                <li>Información de suscripción procesada de forma segura por Stripe</li>
              </ul>

              <h3 className="font-bold text-stone-800 mb-2">2.2 Privacidad de Audio</h3>
              <p>
                Cuando utilizas el dictado por voz, el audio se procesa en tiempo real para transcribir los conceptos del gasto y se descarta inmediatamente. <strong>No almacenamos grabaciones de voz</strong> en nuestros servidores.
              </p>
            </section>

            <section className="pt-6">
              <h2 className="text-lg font-bold text-stone-900 mb-3">3. Cómo Usamos tu Información</h2>
              <ul className="list-disc list-inside space-y-1.5 ml-2 text-stone-600">
                <li>Proporcionar y mantener el registro y cálculo de tus saldos</li>
                <li>Generar análisis y resúmenes automáticos</li>
                <li>Calcular alertas de fechas de corte y límites de pago</li>
                <li>Detectar y prevenir cualquier acceso no autorizado</li>
              </ul>
            </section>

            <section className="pt-6">
              <h2 className="text-lg font-bold text-stone-900 mb-3">4. Cero Venta de Datos</h2>
              <p>
                <strong>Nunca vendemos, alquilamos ni comercializamos tu información financiera o personal con terceros.</strong> Los únicos proveedores externos involucrados son procesadores de infraestructura esenciales (Stripe para pagos seguros cifrados y proveedores de nube con protocolos de cifrado de grado bancario).
              </p>
            </section>

            <section className="pt-6">
              <h2 className="text-lg font-bold text-stone-900 mb-3">5. Seguridad de los Datos</h2>
              <ul className="list-disc list-inside space-y-1.5 ml-2 text-stone-600">
                <li>Cifrado de contraseñas de un solo sentido (bcrypt)</li>
                <li>Comunicaciones protegidas mediante HTTPS/TLS 1.3</li>
                <li>Autenticación segura mediante tokens JWT</li>
                <li>Aislamiento de bases de datos por usuario</li>
              </ul>
            </section>

            <section className="pt-6">
              <h2 className="text-lg font-bold text-stone-900 mb-3">6. Tus Derechos y Eliminación de Cuenta</h2>
              <p className="mb-2">Tienes derecho a acceder, exportar o solicitar la eliminación total e inmediata de tu cuenta y todos tus registros en cualquier momento.</p>
              <p>
                Para ejercer cualquiera de tus derechos, escríbenos directamente a: <a href="mailto:soporte@capacitatu.com" className="text-indigo-600 font-bold hover:underline">soporte@capacitatu.com</a>
              </p>
            </section>

            <section className="pt-6">
              <h2 className="text-lg font-bold text-stone-900 mb-3">7. Contacto</h2>
              <div className="p-4 bg-stone-50 rounded-2xl border border-stone-200 text-xs sm:text-sm">
                <p className="font-bold text-stone-900">VJRDEV Company</p>
                <p className="text-stone-500">Email: <a href="mailto:soporte@capacitatu.com" className="text-indigo-600 hover:underline">soporte@capacitatu.com</a></p>
                <p className="text-stone-500">Sitio: <a href="https://vjrodriguez.dev" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">vjrodriguez.dev</a></p>
              </div>
            </section>
          </div>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
